import { useEffect } from "react";
import idx from "idx";
import useRouter from "found/useRouter";

import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import FormControl from "@material-ui/core/FormControl";
import FormField from "@material-ui/core/TextField";
import FormButton from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";

import graphql from "babel-plugin-relay/macro";
import { useMutation } from "relay-hooks";

import Page from "common/Page";
import useForm from "common/hooks/useForm";
import SnackBar, { openSnackbar } from "common/SnackBar";
import FormFeedback from "common/FormFeedback";
import CircularProgress from "common/FacebookProgress";
import logo from "common/images/Pageant-media.png";

import { ContactUsUserMutationResponse as ContactUsResponse } from "./__generated__/ContactUsUserMutation.graphql";

const mutation = graphql`
  mutation ContactUsUserMutation($input: ContactUsInput!) {
    contactUs(input: $input) {
      success
      error
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    container: {
      width: "500px",
    },
    paper: {
      // marginTop: theme.spacing(8),
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      backgroundColor: "white",
      borderRadius: "8px",
      // padding: theme.spacing(2),
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: "rgb(220, 0, 78)",
    },
    form: {
      width: "100%", // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    card: {
      padding: theme.spacing(2),
    },
    cardHeader: {
      padding: theme.spacing(1, 2),
      textAlign: "center",
    },
    cardTitle: {
      fontSize: "1.25rem",
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    feedback: {
      marginTop: theme.spacing(2),
      textTransform: "capitalize",
    },
    title: {
      fontSize: 14,
    },
    error: {
      textTransform: "capitalize",
    },
    button: {
      // margin: theme.spacing(3, 0, 2),
      color: "white",
    },
    buttonWrapper: {
      position: "relative",
      padding: theme.spacing(2),
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -9,
      marginLeft: -12,
    },
    option: {
      fontSize: 15,
      "& > span": {
        marginRight: 10,
        fontSize: 18,
      },
    },
  })
);

const schema = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  comments: "",
};

// Create your own validationSchema schema property should be the same in
// validationSchema in-order a validation to works in your input.
const validatorSchema = {
  email: {
    required: true,
    validator: {
      func: (value: string) => value.length > 4,
      error: "Email is too short",
    },
  },
  firstName: {
    required: true,
    validator: {
      func: (value: string) => value.length > 2,
      error: "FirstName is too short",
    },
  },
  lastName: {
    required: true,
    validator: {
      func: (value: string) => value.length > 2,
      error: "LastName is too short",
    },
  },
};

interface IProps {
  data?: any | null;
  error?: any | null;
}

export default function ContactUsUser(props: IProps) {
  const classes = useStyles();
  //const { isLoggedin, login, getCurrentUser } = useAuth();
  const { router } = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    disable,
    values,
    errors,
    setError,
    // setValue,
    handleOnChange,
    handleOnSubmit,
  } = useForm(schema, validatorSchema, onSubmitForm);

  const [mutate, { loading }] = useMutation(mutation, {
    onCompleted: ({ contactUs }: ContactUsResponse) => {
      if (contactUs && contactUs.success) {
        openSnackbar("Thank you for contacting us");

        router.push("/");
      }
    },
    onError: (error) => {
      // console.log(error?.errors!)
      setError("error", error);
      openSnackbar("Contactus failed");
    },
  });

  useEffect(() => {
    document.title = "Contact Us";
  }, []);

  // finally call the mutation to save to backend
  function onSubmitForm(data?: any) {
    mutate({
      variables: {
        input: {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          comments: data.comments,
        },
      },
    });
  }

  const errorCheck = (key: string) => {
    const message = idx(
      errors,
      (x: any) => x!.error!.extensions!.validation[key]
    );
    return message ? true : false;
  };

  const hasError = (inputName: string) => !!(errors && errors[inputName]);

  return (
    <Page className={classes.root} title="ContactUs">
      <Container className={classes.container} maxWidth={false}>
        <Box p={1} m={1} justifyContent="center" textAlign="center">
          <img
            className={classes.root}
            src={logo}
            alt={process.env.REACT_APP_SITE_NAME}
          />
        </Box>

        <Box className={classes.paper} boxShadow={7} py={1}>
          <Typography component="h1" variant="h5" gutterBottom>
            Contact Us
          </Typography>
          <SnackBar variant="error" />

          <form onSubmit={handleOnSubmit} className={classes.form}>
            <div className={classes.card}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormField
                    id="firstName"
                    name="firstName"
                    label="First Name"
                    value={values!.firstname}
                    onChange={handleOnChange}
                    variant="outlined"
                    required
                    fullWidth
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    value={values!.lastName}
                    onChange={handleOnChange}
                    variant="outlined"
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    id="email"
                    label="Professional Email"
                    name="email"
                    type="email"
                    value={values!.email}
                    onChange={handleOnChange}
                    error={errorCheck("Email")}
                    variant="outlined"
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormField
                    id="phone"
                    name="phone"
                    label="Phone"
                    value={values!.phone}
                    onChange={handleOnChange}
                    type="tel"
                    variant="outlined"
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item md={12} xs={12} sm={12}>
                  <FormControl component="div" variant="outlined" fullWidth>
                    <TextField
                      id="comments"
                      name="comments"
                      label="Comments"
                      value={values!.comments}
                      onChange={handleOnChange}
                      type="text"
                      variant="outlined"
                      multiline={true}
                      rows={5}
                      placeholder="Post yor message here."
                      fullWidth
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {hasError("error") ? (
                <FormFeedback className={classes.feedback} error>
                  {idx(errors, (x: any) => x?.error?.message!)}
                </FormFeedback>
              ) : null}
            </div>

            <Divider />

            <div className={classes.buttonWrapper}>
              <FormButton
                className={classes.button}
                disabled={disable || loading}
                type="submit"
                // size="large"
                color="primary"
                variant="contained"
                fullWidth
              >
                Submit
              </FormButton>
              {loading && (
                <CircularProgress className={classes.buttonProgress} />
              )}
            </div>
          </form>
        </Box>
      </Container>
    </Page>
  );
}
